// File: ComboPage.tsx

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { useCart } from "../Componants/Header/CartContext";
import { Helmet } from 'react-helmet-async'; // SEO: Import Helmet

// Import your slider images
import img1 from '../assets/hurbal img.webp';
import img2 from '../assets/HurbalWebsiteImage2.webp';
import img3 from '../assets/Img.jpg';

// Placeholder image for products
import product1 from '../assets/Kaaye.png';
import product2 from '../assets/Kalpa.png';
import product3 from '../assets/Prameh.png';
import product4 from '../assets/Vrikka.png';
import product5 from '../assets/JoinpainOil.png';
import product6 from '../assets/JoinPainOilandCapsule.png';
import product7 from '../assets/PrabhatChuran.png';
import product8 from '../assets/Vajshila.png';

// Array of slider data with images, text, and text colors
const sliderData = [
  {
    image: img1,
    title: "Easy Installments with AyushPay",
    subtitle: "Buy your favorite Ayurvedic products with flexible payment options.",
    textColor: "text-white", 
  },
  {
    image: img2,
    title: "Get 20% Off on Your First Order",
    subtitle: "Unlock exclusive discounts and offers on our entire range of products.",
    textColor: "text-green-700", 
  },
  {
    image: img3,
    title: "Free Shipping on All Orders Above ₹500",
    subtitle: "Enjoy the convenience of free doorstep delivery, anywhere in India.",
    textColor: "text-white", 
  },
];

const comboProducts = [
  {
    id: 1001,
    title: 'Kayee Sanvardhan Capsules',
    img: product1,
    price: 1199,
    oldPrice: 1990,
    reviews: 2969,
    seller: "Ayuvya Combos"
  },
  {
    id: 1002,
    title: 'Sudhamrut Kalpa Syrup',
    img: product2,
    price: 1137,
    oldPrice: 1645,
    reviews: 1947,
    seller: "Ayuvya Combos"
  },
  {
    id: 1003,
    title: 'Prameha Shamak Swaras',
    img: product3,
    price: 1033,
    oldPrice: 1898,
    reviews: 1500,
    seller: "Ayuvya Combos"
  },
  {
    id: 1004,
    title: 'Vrikka Shudhi Rasayanam',
    img: product4,
    price: 1200,
    oldPrice: 2000,
    reviews: 2200,
    seller: "Ayuvya Combos"
  },
  {
    id: 1005,
    title: 'Asti Shuddhi Telam',
    img: product5,
    price: 950,
    oldPrice: 1500,
    reviews: 1850,
    seller: "Ayuvya Combos"
  },
  {
    id: 1006,
    title: 'Sandhi Poshak Rasayan',
    img: product6,
    price: 800,
    oldPrice: 1300,
    reviews: 1600,
    seller: "Ayuvya Combos"
  },
  {
    id: 1007,
    title: 'Prabhat Shudhi Churan',
    img: product7,
    price: 750,
    oldPrice: 1200,
    reviews: 1400,
    seller: "Ayuvya Combos"
  },
  {
    id: 1008,
    title: 'Vajra Shila Rasayan',
    img: product8,
    price: 900,
    oldPrice: 1450,
    reviews: 1750,
    seller: "Ayuvya Combos"
  },
];

const ComboPage = () => {
    const [isAddingToCart, setIsAddingToCart] = useState(false);
    const navigate = useNavigate();
    const { addToCart } = useCart();
    const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
    const [textKey, setTextKey] = useState(0); 
    const sliderRef = useRef<HTMLDivElement>(null); 
    const [sliderInView, setSliderInView] = useState(false); 

    useEffect(() => {
        const interval = setInterval(() => {
            setTextKey(prev => prev + 1); 
            setCurrentSlideIndex((prevIndex) => (prevIndex + 1) % sliderData.length);
        }, 5000); 

        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setSliderInView(true);
                    observer.disconnect(); 
                }
            },
            {
                root: null, 
                rootMargin: '0px',
                threshold: 0.1, 
            }
        );

        if (sliderRef.current) {
            observer.observe(sliderRef.current);
        }

        return () => {
            if (sliderRef.current) {
                observer.unobserve(sliderRef.current);
            }
        };
    }, []);


    const handleAddToCart = (product: typeof comboProducts[0]) => {
        setIsAddingToCart(true);
        addToCart(
            {
                productId: product.id,
                productName: product.title,
                price: product.price,
                imageUrl: product.img,
                seller: product.seller
            },
            1
        );
        toast.success(`${product.title} added to cart!`);
        setIsAddingToCart(false);
    };

    const currentSlide = sliderData[currentSlideIndex];

    // SEO: Schema Markup for List of Products (Rich Snippets)
    const itemListSchema = {
        "@context": "https://schema.org",
        "@type": "ItemList",
        "itemListElement": comboProducts.map((product, index) => ({
            "@type": "ListItem",
            "position": index + 1,
            "item": {
                "@type": "Product",
                "name": product.title,
                "image": product.img, // In real app, this should be a full URL
                "offers": {
                    "@type": "Offer",
                    "price": product.price,
                    "priceCurrency": "INR"
                }
            }
        }))
    };

    return (
        <div className="bg-gray-50 py-6 sm:py-10 px-3 sm:px-8 md:px-12">
            {/* SEO: Helmet for Title, Description and Schema */}
            <Helmet>
                <title>Best Ayurvedic Combo Packs & Herbal Health Kits | Ayush Vatika</title>
                <meta name="description" content="Shop exclusive Ayurvedic combo packs and herbal health kits at best prices. Get deals on Ashwagandha, Shilajit, and immunity booster combos. Free Shipping." />
                <meta name="keywords" content="Ayurvedic combo packs, herbal health kits, buy ayurveda online, discount herbal products, immunity booster combo" />
                <link rel="canonical" href="https://ayushvatika.com/combos" />
                <script type="application/ld+json">
                    {JSON.stringify(itemListSchema)}
                </script>
            </Helmet>

            {/* Header Section */}
            <div className="flex justify-between items-center mb-6 sm:mb-8">
                {/* SEO: Changed H2 to H1 for better structure */}
                <h1 className="text-xl sm:text-3xl font-semibold text-gray-800">
                    Exclusive Ayurvedic Combo Packs & Herbal Health Kits
                </h1>
            </div>

            {/* Combo Products Grid - Modified for 2 columns on mobile */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
                {comboProducts.map(product => (
                    <div
                        key={product.id}
                        className="bg-white p-2 sm:p-4 rounded-xl shadow-sm hover:shadow-lg transition-shadow duration-300 flex flex-col"
                    >
                        {/* Product Image */}
                        <div className="relative w-full h-40 sm:h-56 rounded-lg overflow-hidden mb-2 sm:mb-4 bg-gray-100">
                            <img
                                src={product.img}
                                // SEO: Descriptive Alt Text
                                alt={`${product.title} - Ayurvedic Combo Pack`}
                                className="w-full h-full object-cover"
                            />
                        </div>

                        {/* Product Details */}
                        <div className="text-center flex-grow flex flex-col justify-between">
                            <div>
                                <h3 className="text-sm sm:text-lg font-semibold text-gray-800 mb-1 leading-tight line-clamp-2">{product.title}</h3>
                                <div className="hidden sm:flex items-center justify-center text-yellow-500 mb-1">
                                    {'★'.repeat(5)}
                                    <span className="text-gray-600 text-sm ml-2">({product.reviews} Reviews)</span>
                                </div>

                                <div className="flex flex-wrap items-center justify-center gap-1 sm:gap-2 mb-2 sm:mb-3">
                                    <span className="text-sm sm:text-xl font-bold text-black">₹{product.price}</span>
                                    <span className="line-through text-gray-500 text-xs sm:text-sm">₹{product.oldPrice}</span>
                                    <div className="text-green-600 text-xs sm:text-sm hidden sm:block">
                                        (Save ₹{product.oldPrice - product.price})
                                    </div>
                                </div>
                            </div>

                            <button
                                onClick={() => handleAddToCart(product)}
                                className="w-full bg-green-800 text-white py-1.5 sm:py-2 rounded-full hover:bg-green-900 transition text-xs sm:text-base"
                                disabled={isAddingToCart}
                                aria-label={`Add ${product.title} to cart`}
                            >
                                Add to Cart
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            {/* AyushPay Slider Section */}
            <div
                ref={sliderRef} 
                className="w-full h-48 sm:h-64 md:h-96 flex items-center justify-start relative px-4 sm:px-12 mt-8 sm:mt-12 overflow-hidden rounded-lg"
            >
                {/* Image Slider */}
                {sliderData.map((slide, index) => (
                    <img
                        key={index}
                        src={slide.image}
                        // SEO: Descriptive Alt text for sliders
                        alt={`Ayurvedic Offer: ${slide.title}`}
                        className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ease-in-out ${
                            index === currentSlideIndex ? "opacity-100" : "opacity-0"
                        }`}
                    />
                ))}

                {/* Text on top of image */}
                <div
                    key={textKey} 
                    className={`relative z-10 text-left max-w-lg ${currentSlide.textColor} ${
                        sliderInView ? 'animate-slide-in-left' : 'opacity-0'
                    }`}
                >
                    <h2 className="text-xl md:text-4xl font-bold drop-shadow-md">
                        {currentSlide.title}
                    </h2>
                    <p className="mt-2 sm:mt-4 text-sm sm:text-lg drop-shadow-md">
                        {currentSlide.subtitle}
                    </p>
                </div>
            </div>
        </div>
    );
};

export default ComboPage;